// THE FULL-WINDOW VIEWER'S VIDEO AND AUDIO (busy-my-web#639; Figma "Task creation design", frames
// "video view" 40014356:382673, "Audio view 1" 40014356:382752, "Audio view 2" 40014358:382816).
//
// The viewer (components/SmartBox/FileViewer.vue) drew a PDF and a picture; a stored video or audio
// file was downloaded, as anything the browser could not draw always was (lib/attachmentOpen.js).
// The frames draw both — a 16:9 player card with its own controls, and a white audio card with a
// cover, a waveform and a −10 / play / +10 row — so they now open in the viewer like a PDF does.
//
// This module is every rule of the two players that is not drawing: which files are media, the
// "1:24" clock, the playback-rate cycle, the waveform (the frame's static bars, and the real one
// bucketed from the decoded audio), how far a click along a track seeks, the audio card's title and
// meta line, and how big the video card may be in the window. No component, no store; the one
// network-ish thing (`decodeWaveform`) takes its fetch and its AudioContext injected, so every line
// of it runs under a test without a browser's audio stack.
//
// Deliberately NOT here (no data source in the app, 2026-10-20):
//   • the video frame's "Transcript" control — nothing transcribes a stored video;
//   • the audio frames' "Lyrics" chip and frame 03's dark lyrics panel — nothing holds lyrics;
//   • real cover art — an uploaded audio file carries none (`coverUrlOf` below takes one if a record
//     ever does).

// ── Which files are media ────────────────────────────────────────────────────────────────────────

/** Extensions read when the content type does not say (spec: mime first, extension second). */
export const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v']
export const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav', 'ogg', 'oga', 'aac']

/** The content type a media file's bytes should be wrapped in when the store kept none. */
const EXT_MIME = {
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  m4v: 'video/mp4',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  aac: 'audio/aac',
}

const extOf = (name) => {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || '').trim())
  return m ? m[1].toLowerCase() : ''
}

/**
 * 'video' / 'audio' / null for a file, by its content type first and its name's extension second.
 * A content type that names ANOTHER drawable kind (a picture, a PDF, text) wins over the name — the
 * extension is only asked when the type is missing or generic (octet-stream, application/mp4 …).
 *
 * @param {string} contentType
 * @param {string} name
 * @returns {'video'|'audio'|null}
 */
export function mediaKindOf(contentType, name) {
  const mime = String(contentType || '').split(';')[0].trim().toLowerCase()
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('image/') || mime.startsWith('text/') || mime === 'application/pdf') return null
  const ext = extOf(name)
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video'
  if (AUDIO_EXTENSIONS.includes(ext)) return 'audio'
  return null
}

/** The media content type a file NAME implies ('' when it is not a media name). */
export function mediaMimeOf(name) {
  return EXT_MIME[extOf(name)] || ''
}

// ── The clock ────────────────────────────────────────────────────────────────────────────────────

/** A usable duration / position in seconds, or 0 (NaN while loading, Infinity for a live stream). */
export function finiteSeconds(v) {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** "1:24", "6:12", "1:02:03" — the frames' clock. "0:00" for nothing known. */
export function formatMediaTime(seconds) {
  const total = Math.floor(finiteSeconds(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}

// ── Playback rate ("1×", both frames) ────────────────────────────────────────────────────────────

/** The cycle a click on "1×" walks: 1× → 1.5× → 2× → 0.5× → 1×. */
export const PLAYBACK_RATES = [1, 1.5, 2, 0.5]

/** The rate after `rate` in the cycle; an off-cycle rate goes back to 1×. */
export function nextRate(rate) {
  const at = PLAYBACK_RATES.indexOf(Number(rate))
  return at < 0 ? 1 : PLAYBACK_RATES[(at + 1) % PLAYBACK_RATES.length]
}

/** "1×", "1.5×", "0.5×" */
export function rateText(rate) {
  const n = Number(rate)
  return `${Number.isFinite(n) && n > 0 ? n : 1}×`
}

// ── Seeking ──────────────────────────────────────────────────────────────────────────────────────

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))

/** How far along a track (0–1) a pointer at `clientX` is, from the track's bounding rect. */
export function trackFraction(clientX, rect) {
  const width = Number(rect?.width) || 0
  if (width <= 0) return 0
  return clamp(((Number(clientX) || 0) - (Number(rect.left) || 0)) / width, 0, 1)
}

/** The fraction of the file played (0–1) — the scrub track's fill and the knob's place. */
export function playedFraction(current, duration) {
  const d = finiteSeconds(duration)
  return d ? clamp(finiteSeconds(current) / d, 0, 1) : 0
}

/** The position `delta` seconds from `current` (the audio card's −10 / +10), kept inside the file. */
export function seekBy(current, delta, duration) {
  const d = finiteSeconds(duration)
  const next = finiteSeconds(current) + (Number(delta) || 0)
  return d ? clamp(next, 0, d) : Math.max(0, next)
}

// ── The waveform (frames 02 / 03) ────────────────────────────────────────────────────────────────

/** 75 bars, 3px wide, 3px apart, 8–34px tall (frame 02). */
export const WAVEFORM_BARS = 75
export const WAVEFORM_MIN = 8
export const WAVEFORM_MAX = 34

/**
 * The frame's own bar heights, in order — drawn while the real waveform is being decoded, and
 * instead of it when decoding fails or is skipped (see `WAVEFORM_DECODE_MAX_BYTES`).
 */
export const PLACEHOLDER_WAVEFORM = Object.freeze([
  8, 22, 24, 33, 32, 33, 28, 22, 16, 14, 23, 21, 28, 22, 25, 19, 18, 12, 18, 24, 27, 32, 29, 30, 19, 18, 13, 25, 28, 34,
  34, 31, 27, 18, 14, 16, 27, 25, 31, 25, 25, 19, 15, 15, 19, 25, 25, 30, 24, 26, 16, 14, 18, 28, 31, 34, 34, 28, 25, 13,
  18, 20, 31, 29, 33, 27, 24, 18, 12, 19, 20, 25, 21, 27, 20,
])

/**
 * The loudness (RMS) of each of `bars` equal slices of a channel's samples. A slice with no samples
 * (a file shorter than the bar count in samples) is 0. Every sample is not read: a long file is
 * sampled at most ~2000 times per slice, which keeps a 4-minute song well under a frame's budget.
 *
 * @param {ArrayLike<number>} samples
 * @param {number} [bars]
 * @returns {number[]}
 */
export function bucketLevels(samples, bars = WAVEFORM_BARS) {
  const data = samples || []
  const n = data.length || 0
  const count = Math.max(1, Math.floor(Number(bars) || 0))
  const out = []
  for (let b = 0; b < count; b += 1) {
    const start = Math.floor((b * n) / count)
    const end = Math.floor(((b + 1) * n) / count)
    const span = end - start
    if (span <= 0) { out.push(0); continue }
    const stride = Math.max(1, Math.floor(span / 2000))
    let sum = 0
    let seen = 0
    for (let i = start; i < end; i += stride) { const v = Number(data[i]) || 0; sum += v * v; seen += 1 }
    out.push(Math.sqrt(sum / seen))
  }
  return out
}

/**
 * Levels → bar heights between `min` and `max` px, scaled to the loudest slice (so a quiet
 * recording still fills the card). All-silent audio draws every bar at `min`.
 */
export function levelsToHeights(levels, min = WAVEFORM_MIN, max = WAVEFORM_MAX) {
  const list = Array.isArray(levels) ? levels : []
  const peak = list.reduce((m, v) => Math.max(m, Number(v) || 0), 0)
  return list.map((v) => (peak > 0 ? Math.round(min + ((Number(v) || 0) / peak) * (max - min)) : min))
}

/** One mono channel out of a decoded AudioBuffer — the average of its channels. */
export function monoSamples(buffer) {
  const channels = Math.max(0, Number(buffer?.numberOfChannels) || 0)
  if (!channels) return new Float32Array(0)
  const first = buffer.getChannelData(0)
  if (channels === 1) return first
  const out = new Float32Array(first.length)
  for (let c = 0; c < channels; c += 1) {
    const data = buffer.getChannelData(c)
    for (let i = 0; i < out.length; i += 1) out[i] += (data[i] || 0) / channels
  }
  return out
}

/**
 * Bigger than this, the waveform is not decoded: decodeAudioData expands to raw PCM (a 1-hour
 * podcast would be over a gigabyte of floats), and the frame's static bars are a fair stand-in.
 */
export const WAVEFORM_DECODE_MAX_BYTES = 25 * 1024 * 1024

/**
 * The real waveform of the audio at `url`: fetched (a blob URL — the bytes are already in the page),
 * decoded with Web Audio, bucketed into `bars` heights. Resolves to null — the caller keeps the
 * placeholder — when there is no Web Audio, the file is too big, or anything fails.
 *
 * @param {string} url
 * @param {{ fetchImpl?: function, AudioContextCtor?: function, bars?: number, maxBytes?: number }} [deps]
 * @returns {Promise<number[]|null>}
 */
export async function decodeWaveform(url, {
  fetchImpl = (typeof fetch === 'function' ? fetch : null),
  AudioContextCtor = (typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null),
  bars = WAVEFORM_BARS,
  maxBytes = WAVEFORM_DECODE_MAX_BYTES,
} = {}) {
  if (!url || !fetchImpl || !AudioContextCtor) return null
  let ctx = null
  try {
    const res = await fetchImpl(url)
    const bytes = await res.arrayBuffer()
    if (!bytes || bytes.byteLength > maxBytes) return null
    ctx = new AudioContextCtor()
    const buffer = await ctx.decodeAudioData(bytes)
    return levelsToHeights(bucketLevels(monoSamples(buffer), bars))
  } catch {
    return null
  } finally {
    if (ctx && typeof ctx.close === 'function') { try { await ctx.close() } catch { /* already closed */ } }
  }
}

/** How many bars of `bars` are "played" (#3741F3) at `current` of `duration`. */
export function playedBars(current, duration, bars = WAVEFORM_BARS) {
  return Math.round(playedFraction(current, duration) * (Number(bars) || 0))
}

// ── The audio card's words (frames 02 / 03) ──────────────────────────────────────────────────────

/** "Easyonme" — the file name without its extension (frame 02's title). */
export function audioTitle(name) {
  const s = String(name || '').trim()
  const dot = s.lastIndexOf('.')
  return dot > 0 ? s.slice(0, dot) : s
}

/**
 * "Audio · 3:44 · 4.2 MB" — the parts that are known, in that order. `size` is already a text
 * ("4.2 MB", lib/briefings/image `formatBytes`) or ''.
 */
export function audioMetaLine({ duration = 0, size = '' } = {}) {
  const d = finiteSeconds(duration)
  return ['Audio', d ? formatMediaTime(d) : '', String(size || '').trim()].filter(Boolean).join(' · ')
}

/** A cover / thumbnail URL a record may carry one day; '' today (no audio upload has one). */
export function coverUrlOf(doc) {
  return String(doc?.coverUrl || doc?.thumbnailUrl || doc?.thumbnailLink || '').trim()
}

// ── The video card's size (frame 01) ─────────────────────────────────────────────────────────────

/** The frame's card, 1036 × 583 (16:9), and the room it keeps from the window's edges. */
export const VIDEO_MAX_WIDTH = 1036
export const VIDEO_RATIO = 16 / 9
// Each side: the prev / next disc (24 + 44) and a gap; top: the 64px bar; bottom: matching air.
const VIDEO_SIDE_ROOM = 96
const VIDEO_TOP_ROOM = 64
const VIDEO_BOTTOM_ROOM = 48

/**
 * The video card's width and height in the window: as big as the frame's 1036 × 583 allows, scaled
 * down to fit with the margins the arrows and the bar need — 16:9 always. On a phone the card is the
 * screen's full width (no phone frame exists; spec 2026-10-20).
 *
 * @param {number} vw window width
 * @param {number} vh window height
 * @param {{ phone?: boolean }} [opts]
 * @returns {{ width:number, height:number }}
 */
export function videoCardSize(vw, vh, { phone = false } = {}) {
  const w = Math.max(0, Number(vw) || 0)
  const h = Math.max(0, Number(vh) || 0)
  const width = phone
    ? w
    : Math.min(VIDEO_MAX_WIDTH, w - VIDEO_SIDE_ROOM * 2, (h - VIDEO_TOP_ROOM - VIDEO_BOTTOM_ROOM) * VIDEO_RATIO)
  const safe = Math.max(0, Math.floor(width))
  return { width: safe, height: Math.round(safe / VIDEO_RATIO) }
}

// ── Keys ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Is the key event aimed at something that types (an input, a textarea, a select, an editable
 * element)? Space must stay a space there and never toggle playback.
 */
export function isTypingTarget(target) {
  if (!target) return false
  const tag = String(target.tagName || '').toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return !!target.isContentEditable
}
